// Prévia de parcelamento no front — espelha as regras puras do backend
// (`domain.SplitInstallments` e `domain.AddMonthsClamped`), para a tela mostrar exatamente o que
// o POST /transactions/installments vai criar. Datas são strings "AAAA-MM-DD" tratadas como texto
// (nunca `new Date('AAAA-MM-DD')`, que recua um dia em UTC−3).
import { pad2 } from '@components/dateInputUtils';

/**
 * Divide `totalCents` em `n` parcelas inteiras. A sobra de centavos vai para a PRIMEIRA parcela;
 * o sinal do total é preservado (−10000 em 3 → [−3334, −3333, −3333]). `n < 1` devolve [total].
 */
export const splitInstallmentsCents = (totalCents: number, n: number): number[] => {
  const total = Math.trunc(totalCents);
  const count = Math.max(1, Math.trunc(n));
  if (count === 1) return [total];
  const base = Math.trunc(total / count);
  const remainder = total - base * count;
  const parts = Array.from({ length: count }, () => base);
  parts[0] = base + remainder;
  return parts;
};

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Soma `months` meses a "AAAA-MM-DD" mantendo o dia; quando o mês de destino é mais curto, cai no
 * último dia (31/01 + 1 → 28/02 ou 29/02). Devolve a entrada inalterada se ela for inválida.
 */
export const addMonthsClamped = (dateISO: string, months: number): string => {
  const m = ISO.exec(dateISO);
  if (!m) return dateISO;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const index = year * 12 + (month - 1) + Math.trunc(months);
  const newYear = Math.floor(index / 12);
  const newMonth0 = index - newYear * 12;
  const lastDay = new Date(newYear, newMonth0 + 1, 0).getDate();
  return `${newYear}-${pad2(newMonth0 + 1)}-${pad2(Math.min(day, lastDay))}`;
};

export interface InstallmentPreview {
  index: number;
  date: string;
  amount_cents: number;
}

/** Lista das parcelas (1..n) com data mensal a partir de `firstDate` e valores de `splitInstallmentsCents`. */
export const previewInstallments = (totalCents: number, n: number, firstDate: string): InstallmentPreview[] =>
  splitInstallmentsCents(totalCents, n).map((amount, i) => ({
    index: i + 1,
    date: addMonthsClamped(firstDate, i),
    amount_cents: amount,
  }));
